<!--
  The watch route.

  Two agents meet here. The catalogue (this file) knows WHAT is being watched —
  the title, the episode, the artwork, where you got to last time, and which links
  exist. The player (`$lib/player`) owns the screen once a source is in hand.

  So this route does three things and then gets out of the way:
    1. resolves ~50–120 candidate links through /api/resolve and orders them —
       a hand-picked link first, then anything already verified by a header probe,
       then this viewer's quality/language preference;
    2. joins the group when the URL carries one — and OPENS one when it does not:
       the room is always open (auto-invite), so friends can walk in without
       anyone pressing anything, and the only button is Share for the link;
    3. writes the resume point as the film runs.

  The player is imported dynamically. That is not indirection for its own sake: it
  keeps the whole media stack — demuxer, muxer, and the 2.3 MB libav wasm behind it
  — out of the bundle that loads when you are only browsing.
-->
<script lang="ts">
  import { onDestroy, untrack, type Component, type Snippet } from 'svelte';
  import type { Candidate, ContentRef, Episode, ProbedSource } from '$types';
  // Type-only, so they are erased at build time and cost the browse bundle nothing.
  import type { PlayerController } from '$lib/player';
  import type { ChatTransport } from '$lib/chat';
  // Direct path, never `$lib/player`: that barrel drags the whole media stack in and
  // this screen imports the player dynamically on purpose. See `player/index.ts`.
  import { chrome } from '$lib/player/chrome';
  import { stageInset } from '$lib/player/stage-inset';
  import RoomInviteSheet from '$lib/social/RoomInviteSheet.svelte';
  import { watchRoomAccess } from '$lib/social/room-access';
  import { watchSession } from '$lib/sync/watch-session';
  import type { SyncEvent } from '$lib/sync/state';
  import WatchViewport from '$lib/ui/WatchViewport.svelte';
  import ArtworkWaiting from '$lib/player/ArtworkWaiting.svelte';
  import { downloads } from '$lib/offline/manager.svelte';
  import { isOfflineMode } from '$lib/ui/session.svelte';
  import { downloadKey } from '$lib/offline/index-state';
  import Button from '$lib/ui/Button.svelte';
  import Icon from '$lib/ui/Icon.svelte';
  import SourceSheet from '$lib/ui/SourceSheet.svelte';
  import { newRoomId, resolve, roomSnapshot } from '$lib/ui/api';
  import { detail, findEpisode, posterUrl, backgroundUrl, type TitleDetail } from '$lib/ui/cinemeta';
  import { episodeCode } from '$lib/ui/format';
  import {
    back,
    currentRoute,
    go,
    markRoomMine,
    nav,
    noteRoomEnded,
    roomIsMine,
    setCurrentRoom,
    titleHref,
    watchHref
  } from '$lib/ui/nav.svelte';
  import { pinSource, playOrder, SourceSelector } from '$lib/ui/picker.svelte';
  import { prefs, recordProgress, resumeSec } from '$lib/ui/prefs.svelte';
  import { FILM_BEAT_MS, beatFilmInProgress, endFilmInProgress } from '$lib/ui/trailer.svelte';

  const session = untrack(() => watchSession(currentRoute));
  const route = $derived(nav.route.name === 'watch' ? nav.route : null);
  const imdbId = $derived(route?.imdbId ?? '');
  const kind = $derived(route?.kind ?? 'movie');
  const season = $derived(route?.season ?? null);
  const episodeNo = $derived(route?.episode ?? null);
  const roomId = $derived(route?.roomId ?? null);

  let meta = $state<TitleDetail | null>(null);
  let metaError = $state('');

  /**
   * The placeholder candidate a download mounts the player with.
   *
   * EVERY FIELD MATTERS. The first cut left out audioHints, langHints and
   * cachedFlag, and `setCandidates` crashed on prod with "Cannot read
   * properties of undefined (reading 'find')" before the controller ever
   * reached the local file: the player opened on a black screen offline. The
   * type said `as unknown as Candidate`, which is exactly how it got through
   * a typecheck. Build it as a real Candidate instead, so the compiler is
   * the one enforcing this.
   */
  function localPlaceholder(ref: ContentRef): Candidate {
    return {
      id: 'opfs:local',
      provider: 'local' as Candidate['provider'],
      title: ref.title,
      quality: 'unknown' as Candidate['quality'],
      sizeBytes: 0,
      bitrateBps: 0,
      audioHints: [],
      langHints: [],
      url: 'opfs:local',
      cachedFlag: true
    };
  }

  /** A TitleDetail-shaped stand-in built from what the download index kept. */
  function offlineTitle(k: ContentRef['kind'], id: string): NonNullable<typeof meta> | null {
    const mine = downloads.items.filter((e) => e.ref.kind === k && e.ref.imdbId === id);
    if (mine.length === 0) return null;
    const first = mine[0]!;
    const seasons = k === 'series'
      ? [...new Set(mine.map((e) => e.ref.season ?? 1))].sort((a, b) => a - b).map((season) => ({
          season,
          episodes: mine
            .filter((e) => (e.ref.season ?? 1) === season)
            .sort((a, b) => (a.ref.episode ?? 0) - (b.ref.episode ?? 0))
            .map((e) => ({
              season,
              episode: e.ref.episode ?? 1,
              title: e.episodeLabel?.replace(/^S\d+ E\d+ · /, '') ?? `Episode ${e.ref.episode ?? 1}`
            }))
        }))
      : [];
    return { id, kind: k, name: first.title, poster: first.poster, seasons } as unknown as NonNullable<typeof meta>;
  }
  let candidates = $state<Candidate[]>([]);
  let linksError = $state('');
  let searching = $state(true);
  let pickerOpen = $state(false);
  let inviteOpen = $state(false);
  let inviteRoom = $state('');
  let roomError = $state('');
  $effect(() => {
    const id = roomId;
    roomError = '';
    if (!id) return;
    return watchRoomAccess(() => import('$lib/player/sync-shared'), message => {
      roomError = message;
      setCurrentRoom(null);
    });
  });

  /**
   * Everything that identifies WHAT is being watched, as one primitive string.
   *
   * The expensive effects below key on THIS and never on the derived `content`
   * object. `nav.route` is replaced wholesale on every navigation, so the URL
   * gaining `?room=` — which is what forming a group does — produced a brand new
   * ref object with identical fields. That restarted the metadata fetch and the
   * candidate hunt, and the running player was torn down and re-mounted: joining
   * your own friends restarted the film. A room is not a change of film.
   */
  const contentKey = $derived(imdbId ? `${kind}|${imdbId}|${season ?? ''}|${episodeNo ?? ''}` : '');

  /* ── 1. What is this ──────────────────────────────────────────────────── */

  $effect(() => {
    const key = contentKey;
    if (!key) return;
    const [k, id] = key.split('|') as [ContentRef['kind'], string];
    const abort = new AbortController();
    meta = null;
    metaError = '';
    void detail(k, id, abort.signal)
      .then((found) => {
        if (!abort.signal.aborted) meta = found;
      })
      .catch((err: unknown) => {
        if ((err as Error)?.name === 'AbortError') return;
        /*
         * THE DOWNLOAD IS ITS OWN METADATA. Offline, the catalogue cannot
         * answer - but the Downloads index remembers the title and the
         * episode label it was saved with, and that is all this screen needs
         * to name the thing and mount the player. Only when there is no
         * download either is this an error the viewer should see.
         */
        const local = offlineTitle(k, id);
        if (local) {
          meta = local;
          return;
        }
        metaError = (err as Error).message || 'Could not load this title.';
      });
    return () => abort.abort();
  });

  const episode = $derived<Episode | undefined>(
    meta && kind === 'series' && season !== null && episodeNo !== null
      ? findEpisode(meta.seasons, season, episodeNo)
      : undefined
  );

  // Same fields in, same REFERENCE out. This object is the player's prop and the
  // input to `resumeSec`/`playOrder`; handing out a fresh one on every navigation
  // makes identity-sensitive consumers think the film changed when it did not.
  let lastContent: ContentRef | null = null;
  const content = $derived.by((): ContentRef | null => {
    if (!imdbId) {
      lastContent = null;
      return null;
    }
    const next: ContentRef = {
      kind,
      imdbId,
      title: meta?.name ?? imdbId,
      ...(kind === 'series' ? { season: season ?? 1, episode: episodeNo ?? 1 } : {})
    };
    const prev = lastContent;
    if (
      prev &&
      prev.kind === next.kind &&
      prev.imdbId === next.imdbId &&
      prev.title === next.title &&
      prev.season === next.season &&
      prev.episode === next.episode
    ) {
      return prev;
    }
    lastContent = next;
    return next;
  });

  const heading = $derived(meta?.name ?? '');
  const subheading = $derived(
    kind === 'series' && season !== null && episodeNo !== null
      ? `${episodeCode(season, episodeNo)}${episode ? ` · ${episode.title}` : ''}`
      : [meta?.year, meta?.runtimeText].filter(Boolean).join(' · ')
  );
  const poster = $derived(episode?.thumbnail ?? meta?.poster ?? (imdbId ? posterUrl(imdbId) : undefined));
  /**
   * LANDSCAPE ART FOR THE LOADING SCREEN, which is a landscape hole.
   *
   * The episode's own still first — it is per-episode, so it is the most specific
   * image the catalogue has — then the title's backdrop. `detail()` synthesises a
   * `background` when Cinemeta has none (see `$lib/ui/cinemeta`), so this is set for
   * essentially everything; the player falls back to the portrait `poster` only when
   * it genuinely is not.
   */
  const backdrop = $derived(episode?.thumbnail ?? meta?.background ?? (imdbId ? backgroundUrl(imdbId) : undefined));

  /* ── 2. Which bytes ───────────────────────────────────────────────────── */

  // The picker's own selector. Used for the sheet and for the silent verification
  // that decides which candidate the player is handed first.
  let selector = $state<SourceSelector | null>(null);

  // Deliberately waits for the metadata: this effect's dependencies ARE the
  // content ref, so starting before the title is known would abort its own
  // request the moment the name arrived. It runs exactly once per episode.
  $effect(() => {
    // Keyed on the film, plus "do we have metadata yet" — nothing else. `content`
    // is read untracked so its identity cannot restart a 6–10 s candidate hunt.
    const key = contentKey;
    const haveMeta = meta !== null;
    if (!key || !haveMeta) return;
    const ref = untrack(() => content);
    if (!ref) return;

    const abort = new AbortController();
    searching = true;
    linksError = '';
    candidates = [];

    void resolve(ref, { signal: abort.signal })
      .then((result) => {
        if (abort.signal.aborted) return;
        candidates = playOrder(ref, result.candidates);
        searching = false;
        if (result.candidates.length === 0) {
          linksError = 'This title isn’t available to watch right now.';
        }
      })
      .catch((err: unknown) => {
        if ((err as Error)?.name === 'AbortError') return;
        searching = false;
        linksError = 'We couldn’t start this title. Please try again.';
      });

    return () => abort.abort();
  });

  /**
   * While the picker is up, the player is held back. Otherwise it would mount
   * underneath, start its own probing, and the two would race for the same links.
   */
  let holdForPick = $state(false);

  /** Built on demand: the sheet is the only thing that needs it. */
  function openPicker(): void {
    const ref = content;
    if (!ref) return;
    holdForPick = true;
    pickerOpen = true;
    if (selector) return;

    const made = new SourceSelector(ref, {
      onSelect: ({ candidate }) => {
        // A hand-picked, verified link goes to the front; the player will try it
        // first and its own probe will find the header already close at hand.
        pinSource(ref, candidate);
        candidates = playOrder(ref, candidates.length ? candidates : [candidate]);
      }
    });
    selector = made;
    // Reuse what this screen already resolved rather than asking again.
    void (candidates.length > 0 ? made.adopt(candidates) : made.start());
  }

  // `?pick=1` — "Sources" from the detail page — opens straight into the list.
  let pickRequested = $state(false);
  $effect(() => {
    if (route?.pick && meta && !pickRequested) {
      pickRequested = true;
      openPicker();
    }
  });

  function closePicker(): void {
    pickerOpen = false;
    holdForPick = false;
  }

  $effect(() => {
    const current = selector;
    return () => current?.dispose();
  });

  /* ── 3. The player ────────────────────────────────────────────────────── */

  // Resolves to {} when the module is not in the build, so the catalogue always
  // compiles on its own.
  const playerModule = import.meta.glob('/src/lib/player/index.ts');

  /** What this screen actually hands the player. Typed so the `overlay` snippet's
   *  parameter is the real controller and not an implicit `any`. */
  type PlayerProps = {
    content: ContentRef;
    candidates: Candidate[];
    candidate?: Candidate | undefined;
    probe?: ProbedSource | undefined;
    roomId?: string | null;
    title?: string;
    subtitle?: string;
    poster?: string | undefined;
    backdrop?: string | undefined;
    startSec?: number;
    onprogress?: (positionSec: number, durationSec: number) => void;
    onback?: () => void;
    onepisode?: (content: ContentRef) => void;
    overlay?: Snippet<[PlayerController]>;
  };

  type PlayerModule = {
    Player?: Component<PlayerProps>;
    default?: Component<PlayerProps>;
    sharedSync?: () => {
      joinRoom: (id: string, name?: string) => void;
      setName: (name: string) => void;
      setPresent: (present: boolean) => void;
      /** Sync events. Only `room-ended` is read here; see the effect below. */
      on: (fn: (e: SyncEvent) => void) => () => void;
    };
  };

  let Player = $state<Component<PlayerProps> | null>(null);
  let playerMissing = $state(false);
  /**
   * The chunk FAILED TO ARRIVE — which is not the same thing as the module not
   * existing. The classic cause is a tab that lived across a deploy: its file
   * list names a player chunk the server no longer has (two real phones hit
   * exactly this, 2026-08-17, and were told "the player is not in this build",
   * which was a lie). main.ts's vite:preloadError guard reloads once for this
   * by itself; this state is the honest screen for whatever gets past it.
   */
  let playerStale = $state(false);
  let syncFor = $state<PlayerModule['sharedSync'] | null>(null);

  $effect(() => {
    const load = playerModule['/src/lib/player/index.ts'];
    if (!load) {
      playerMissing = true;
      return;
    }
    const adopt = (mod: unknown): void => {
      const m = mod as PlayerModule;
      const component = m.Player ?? m.default;
      if (component) Player = component;
      else playerMissing = true;
      if (m.sharedSync) syncFor = m.sharedSync;
    };
    void load()
      .then(adopt)
      .catch(
        // One quiet retry: a flaky radio moment shouldn't need a human.
        () =>
          new Promise((r) => setTimeout(r, 1200)).then(() =>
            load()
              .then(adopt)
              .catch(() => (playerStale = true))
          )
      );
  });

  /* ── The call window ──────────────────────────────────────────────────── */

  // Loaded exactly like the player, and for the same reason: browsing must not pay
  // for the WebRTC stack. Only fetched once there is a group to call into, and a
  // failure here is silent — a broken call layer must never stop a film.
  const callsModule = import.meta.glob('/src/lib/calls/index.ts');
  type DockProps = { onspeaking?: (talking: boolean) => void; oncapture?: (capturing: boolean) => void };
  let CallDock = $state<Component<DockProps> | null>(null);

  $effect(() => {
    if (!roomId || CallDock) return;
    const load = callsModule['/src/lib/calls/index.ts'];
    if (!load) return;
    void load()
      .then((mod) => {
        const m = mod as { CallDock?: Component<DockProps> };
        if (m.CallDock) CallDock = m.CallDock;
      })
      .catch(() => {
        /* no call layer in this build — the film still plays */
      });
  });

  /* ── The room's chat ──────────────────────────────────────────────────── */

  /**
   * Mounted here and nowhere else, because THIS is where the room socket exists:
   * `sharedSync()` has joined by the time a `?room=` watch screen is on screen,
   * and chat rides that one socket rather than opening a second (a second socket
   * is a second member with the same face — see `$lib/chat/transport.ts`).
   *
   * It is deliberately OUTSIDE the player's subtree, as a sibling of the room
   * pill: the player owns its own chrome, its idle timer and its full-screen
   * element, and a drawer of ours inside any of that would be a second owner of
   * things that already have one.
   *
   * Loaded dynamically for the same reason the player and the call dock are: a
   * solo viewing never downloads it.
   *
   * IT IS INERT UNTIL THE SYNC ENGINE GROWS A RAW-FRAME HOOK — six additive lines
   * written out in full in `web/src/lib/chat/transport.ts`, in a file that is not
   * this agent's to edit. Until they land the panel mounts, renders its empty
   * state and warns once in the console, which is exactly what it is designed to
   * do; nothing here changes when they arrive.
   */
  const chatModule = import.meta.glob('/src/lib/chat/index.ts');
  type ChatProps = {
    transport: ChatTransport;
    roomId: string | null;
    selfMemberId?: string | null;
    open?: boolean;
    onunread?: (count: number) => void;
    heading?: string;
    compact?: boolean;
  };
  let ChatPanel = $state<Component<ChatProps> | null>(null);
  let chatTransport = $state<ChatTransport | null>(null);
  let chatOpen = $state(false);
  let unread = $state(0);

  $effect(() => {
    const make = syncFor;
    if (!roomId || !make || ChatPanel) return;
    const load = chatModule['/src/lib/chat/index.ts'];
    if (!load) return;
    void load()
      .then((mod) => {
        const m = mod as {
          ChatPanel?: Component<ChatProps>;
          syncChatTransport?: (sync: unknown) => ChatTransport;
        };
        if (!m.ChatPanel || !m.syncChatTransport) return;
        chatTransport = m.syncChatTransport(make());
        ChatPanel = m.ChatPanel;
      })
      .catch(() => {
        /* no chat layer in this build — the film still plays */
      });
  });

  // Leaving the group closes the drawer; a panel over a solo film is nonsense.
  $effect(() => {
    if (!roomId) chatOpen = false;
  });

  /**
   * THE CHIPS RIDE ON THE FILM, NOT ON THE WINDOW.
   *
   * The room code and the Chat toggle are `position: fixed` against the top-right of
   * the VIEWPORT, and the drawer is fixed to the same corner one layer above — so with
   * the chat open, the drawer painted over both and the only way out was its own ×.
   * That was survivable while the drawer merely covered the picture. Now that the
   * picture SHRINKS out of the drawer's way, chips pinned to the window are also the
   * only thing left sitting on top of a panel the film has politely stepped aside for.
   *
   * So they read the same claim the stage does. `stageInset` already exists for exactly
   * this shape of problem and already has two readers (the player's stage and the call
   * window); this is a third READER, not a second writer — the panel is still the only
   * thing that says how big it is. That also means the suppression rule comes for free:
   * in a DOM full screen the inset publishes zero, which is right, because none of this
   * is painted then anyway.
   */
  let chipInset = $state(0);
  $effect(() => stageInset.subscribe((inset) => (chipInset = inset.right)));

  /**
   * …AND THEY GO WHEN EVERYTHING ELSE GOES.
   *
   * "Nothing over the picture" was measured last round by enumerating the children of
   * the player's own frame, and these are not children of it — they are a fixed-position
   * sibling of the whole player, for the reason above. So with the control bar at an
   * effective opacity of 0, QA found `DIV.chips` still at **1**, a bright pill over the
   * top corner of the film, at 1280×800 and at 390×844 alike. A probe scoped to one
   * subtree cannot see a sibling; the fix is that the sibling reads the same flag.
   *
   * `chrome` is the player's single idle timer and the ONE owner of this flag — there is
   * no second timer here, only a third reader of the first one, exactly as `stageInset`
   * is read rather than re-derived. `subscribe` calls back immediately, so a screen that
   * mounts into an already-idle player starts hidden instead of flashing.
   */
  let chromeVisible = $state(true);
  let playerModalOpen = $state(chrome.modal);
  $effect(() => chrome.subscribe((visible) => (chromeVisible = visible)));
  $effect(() => chrome.onModal((open) => (playerModalOpen = open)));

  /* ── "A film is in progress" ──────────────────────────────────────────── */

  /**
   * One localStorage write every four seconds while this screen is mounted, so
   * that the BROWSE layer in another tab knows not to start a trailer. This
   * app's measured bottleneck is the byte proxy at 7.45 MB/s and two viewers
   * already take half of it; a 1.15 MB YouTube embed competing with a film
   * being watched is exactly the fight nobody wants to lose.
   *
   * Deliberately here and not in the player: the player is a component that a
   * bench page also mounts, whereas THIS screen is the one place a film is
   * actually being watched.
   */
  $effect(() => {
    beatFilmInProgress();
    const beat = setInterval(beatFilmInProgress, FILM_BEAT_MS);
    return () => {
      clearInterval(beat);
      endFilmInProgress();
    };
  });

  const startSec = $derived(content ? resumeSec(content) : 0);
  const ready = $derived(
    !!content && !!Player && !searching && !holdForPick && candidates.length > 0
  );

  /**
   * ONCE THE PLAYER IS UP FOR A TITLE, IT STAYS UP FOR THAT TITLE.
   *
   * The other half of the episode race (App.svelte's `{#key}` is the first). Changing
   * episode moves the address bar, which re-runs this screen's own candidate search, so
   * `searching` goes true, `ready` goes false, and `{#if ready}` unmounts the player —
   * mid-probe, with a `commit()` in flight that would have sent the `setSource`.
   * `detach()` bumps `pickGeneration` and the pin is simply lost.
   *
   * The controller already knows how to move between episodes without being rebuilt
   * (`switchContent`); it just has to survive long enough to do it. So the mount latches
   * per title: it opens when the screen is first ready and only closes when the TITLE
   * changes, which is a genuinely different film and a genuinely new player.
   */
  let mountedFor = $state<string | null>(null);
  $effect(() => {
    if (ready) mountedFor = imdbId;
  });
  const keepMounted = $derived(ready || (mountedFor === imdbId && !!content && !!Player));

  /* ── The group ────────────────────────────────────────────────────────── */

  /**
   * UNTRACKED, and the whole group feature depends on it.
   *
   * `joinRoom` is not a quiet setter: it reduces and calls every subscriber
   * SYNCHRONOUSLY, and one of those subscribers is the player's controller, which
   * both reads and writes its own `$state` on the way through. Called bare from an
   * effect, all of that happens inside THIS effect's tracking context — so the
   * effect subscribes to `stage`, `members`, `syncPhase`… and then writes them.
   * Svelte re-ran it until `effect_update_depth_exceeded` tore the screen down,
   * which is why forming a group after playback started killed the host's UI.
   *
   * It only bit on the in-place solo→room transition: on a cold load with `?room=`
   * the controller has not attached yet, so there are no subscribers to read
   * anything, and the same code was harmless.
   */
  // Check an owned carried code BEFORE joining it. A browse detour intentionally
  // ends a solo room; racing this check against join used to eject the next film.
  let checkedRoom = $state<string | null>(null);
  $effect(() => {
    const id = roomId;
    if (!id || !roomIsMine(id) || untrack(() => checkedRoom === id)) return;
    let stopped = false;
    void roomSnapshot(id).then(snap => {
      if (stopped || session.current()?.roomId !== id) return;
      if (snap.exists === false) void openRoom();
      else checkedRoom = id;
    }).catch(() => {
      if (!stopped && session.current()?.roomId === id) checkedRoom = id;
    });
    return () => { stopped = true; };
  });

  $effect(() => {
    const make = syncFor;
    const id = roomId;
    const label = heading;
    const who = prefs.name.trim();
    if (!make || !id || roomError || (roomIsMine(id) && checkedRoom !== id)) return;
    untrack(() => {
      const sync = make();
      if (who) sync.setName(who);
      // BEFORE the join, so the join itself carries "somebody is in front of a player
      // for this room". A join that arrived without it would create a room the server
      // deletes on the next line, because nobody in it is asserting anything.
      session.hold(id);
      sync.setPresent(true);
      sync.joinRoom(id, label || undefined);
    });
  });

  /**
   * LEAVING THE PLAYER IS LEAVING THE ROOM. The owner's rule, and the only place it is
   * written on this side.
   *
   * *"'Live' must mean a person is actually in the player. The moment you leave the
   * player screen, you are out of the room, and your socket dies. Immediately — no idle
   * timer, no grace period."* He was told a reload loses the room and chose it; the
   * return path lands on the title page, not on an error, and `recordProgress` has
   * already written the resume point (see the effect further down), so the FILM comes
   * back even though the room does not.
   *
   * ITS OWN EFFECT, WITH TWO DEPENDENCIES AND NO MORE. The join effect above also reads
   * `heading` and `prefs.name`, both of which arrive AFTER the metadata fetch — putting
   * the teardown there would have withdrawn and re-asserted the moment the film's title
   * loaded, which is to say it would have deleted the room roughly one second into every
   * session. `roomId` and `syncFor` each change at most once on this screen (null → set),
   * and the first run registers no teardown because there is nothing yet to tear down.
   */
  // App keys Watch by title. Replacing A with B in the same room transfers the
  // presence claim; detaching A's media is not leaving the room. A genuine exit
  // still withdraws immediately, without a timeout or suppressing owner closure.
  onDestroy(() => untrack(() => session.destroy(() => syncFor?.().setPresent(false))));

  $effect(() => {
    const id = roomId;
    if (id) untrack(() => setCurrentRoom(id));
  });

  /**
   * THE ROOM ENDED UNDER US, SO THIS SCREEN GOES.
   *
   * The owner's rule: *"If the host leaves, then that room has to die for everyone."* So
   * a guest's room can end while they are mid-film, having done nothing at all — the
   * server drops it, tells everyone still in it, and the sync engine turns that into
   * `room-ended` ($lib/sync/reducer.ts).
   *
   * THE FIRST VERSION OF THIS LEFT THE FILM PLAYING and drew a dismissible line over it,
   * on the argument that the person in front of this screen had not gone anywhere. The
   * owner watched it happen on an iPad and said the opposite: *"When the host left the
   * room, the guest didn't actually get forced out of the room. I want the guests to get
   * forced out of the player… the iPad was still playing the video. I just want them to
   * get taken out of the player."*
   *
   * So this is an EJECTION. We leave for the title page — the same place the player's own
   * Back button goes, so it is where you would have ended up anyway — and the reducer has
   * already dropped the intent to 0, which means nothing is left playing behind us and
   * nothing autoplays on the way out. `replace`, because the room-bearing entry is no
   * longer somewhere to return to; a Back press must not walk into a dead room.
   *
   * The explanation travels with us rather than dying with this component: `noteRoomEnded`
   * hands it to the title page, which draws it once. An ejection with no sentence attached
   * is indistinguishable from a crash.
   */
  $effect(() => {
    const make = syncFor;
    const id = roomId;
    if (!make || !id) return;
    return untrack(() =>
      session.listen(id, (fn) => make().on(fn), (current) => {
        noteRoomEnded(id);
        go(titleHref(current.kind, current.imdbId, current.season, null), { replace: true });
      })
    );
  });

  /**
   * Open a fresh room for this viewing, without disturbing what is on screen.
   *
   * The player is NOT re-mounted by this: the URL gains `?room=`, which only the
   * room effects depend on, so the film keeps playing and the new room simply
   * adopts it. `replace` because Back should leave the film, not return to a solo
   * copy of a session that is now shared.
   *
   * Returns the room's id so a Share press that had to open the room first can
   * build the link without waiting for the URL to catch up.
   */
  let makingRoom = $state(false);
  async function openRoom(): Promise<string | null> {
    const requested = session.current();
    if (makingRoom || !requested) return null;
    makingRoom = true;
    try {
      const id = await newRoomId();
      const current = session.current();
      if (!current || current.roomId !== requested.roomId) return null;
      // Ours: this tab opened it, which is what licenses the tombstone check in
      // the auto-open effect below when the code is carried to a later film.
      markRoomMine(id);
      checkedRoom = id;
      go(watchHref({ kind: current.kind, imdbId: current.imdbId, season: current.season, episode: current.episode, roomId: id }), { replace: true });
      return id;
    } catch {
      /* the gate or the network; stay solo — Share tries again on press */
      return null;
    } finally {
      makingRoom = false;
    }
  }

  /** The Share press's half: reuse the room we are in, open one only if we are not. */
  async function startGroup(): Promise<string | null> {
    return roomId ?? openRoom();
  }

  /**
   * Starting playback creates a room of one. Its owner account determines privacy;
   * private rooms require explicit invitations from the share sheet.
   *
   * AND A CARRIED CODE IS CHECKED BEFORE IT IS TRUSTED. `watchHref` keeps a tab in
   * its group across films, and a room dies the moment its last player closes
   * (`ownerHolds` in the hub — held by anybody, dead when empty). A solo watcher is
   * that room's only member, so between their films the sticky code in
   * `currentRoom` is usually a tombstone — and joining a tombstone gets `no-room
   * ended:true` and an ejection to the title page for no reason the viewer can
   * see. So a code THIS TAB opened (`roomIsMine`) is snapshot-checked on the way
   * in, and a dead one is quietly replaced with a fresh room. A room somebody
   * ELSE opened is joined as-is: a guest walking back into a room that has since
   * ended should be TOLD it ended, which is exactly what the ejection note does.
   *
   * ONE TRY PER SCREEN, not per state change: `openRoom` toggles `makingRoom`, and
   * an effect that watched it would re-run on failure for ever against a dead
   * network. If this try fails the film still plays alone and the Share button
   * repeats the attempt on press — a person, not a loop, decides to try again.
   */
  let autoRoomTried = false;
  $effect(() => {
    if (!route || autoRoomTried) return;
    const id = roomId;
    autoRoomTried = true;
    untrack(() => {
      if (id === null) {
        /* Offline there is no room server to open one on, and a failed open
           would sit in the socket's reconnect loop under a film that is
           playing perfectly well from the disk. Solo it is. */
        if (isOfflineMode()) return;
        void openRoom();
        return;
      }

    });
  });

  /* ── The resume point ─────────────────────────────────────────────────── */

  // The player reports position at ~0.2 Hz and, once given `onprogress`, stops
  // writing a resume entry of its own. One writer, and it is the one that knows the
  // artwork and the episode title.
  let latest = { positionSec: 0, durationSec: 0 };

  function writeResume(): void {
    const ref = content;
    const info = meta;
    if (!ref || !info) return;
    if (latest.positionSec <= 0 || latest.durationSec <= 0) return;
    recordProgress({
      ref,
      positionSec: latest.positionSec,
      durationSec: latest.durationSec,
      title: info.name,
      ...(episode ? { subtitle: episode.title } : {}),
      ...(info.poster ? { poster: info.poster } : {}),
      ...(info.background ? { background: info.background } : {})
    });
  }

  $effect(() => {
    const onHide = (): void => writeResume();
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      writeResume(); // leaving the screen is exactly when the position matters
    };
  });

  /* ── Sharing ──────────────────────────────────────────────────────────── */

  /** Open the owner-only invitation controls without pausing or remounting the film. */
  async function share(): Promise<void> {
    const id = roomId ?? (await startGroup());
    if (!id) { roomError = 'Could not open a room. Check your connection and try again.'; return; }
    inviteRoom = id;
    inviteOpen = true;
  }

  const exitHref = $derived(titleHref(kind, imdbId, season, null));

</script>

<svelte:head><title>{heading ? `${heading} — Jovii` : 'Jovii'}</title></svelte:head>

<WatchViewport>
{#if inviteOpen && inviteRoom}<RoomInviteSheet bind:open={inviteOpen} roomId={inviteRoom} />{/if}

<!--
  Nothing here draws "the room has ended" any more. This screen is the thing being
  taken away when that happens, so the sentence is handed to the title page we land
  on (`noteRoomEnded` above, `routes/Title.svelte` below). A banner over a player the
  viewer is about to be removed from is a message with nowhere to be read.
-->
{#if keepMounted && Player && content && !roomError && (!roomId || !roomIsMine(roomId) || checkedRoom === roomId)}
  {@const PlayerComponent = Player}
  <div class="host">
    <PlayerComponent
      {content}
      {candidates}
      candidate={selector?.selected?.candidate}
      probe={selector?.selected?.probe}
      {roomId}
      title={heading}
      subtitle={subheading}
      {poster}
      {backdrop}
      {startSec}
      onprogress={(positionSec, durationSec) => {
        latest = { positionSec, durationSec };
        writeResume();
      }}
      onback={() => back(exitHref)}
      onepisode={(next) => {
        /*
         * THE ADDRESS BAR HAS TO FOLLOW THE PLAYER.
         *
         * The player changes episode on its own — the Next Episode countdown, a
         * pick from the episode sheet, a host who moved the room. Without this
         * the URL still names the previous one, so a reload or a Back button
         * goes to the wrong episode while the screen shows the right one.
         *
         * `replace`, not a push: auto-advancing through six episodes must not
         * bury the way out of the player under six Back presses.
         */
        go(
          watchHref({
            kind: 'series',
            imdbId: next.imdbId,
            season: next.season ?? null,
            episode: next.episode ?? null,
            roomId
          }),
          { replace: true }
        );
      }}
    >
      {#snippet overlay(player)}
        {#if CallDock}
          {@const Dock = CallDock}
          <Dock
            onspeaking={(talking) => player.setDucking(talking)}
            oncapture={(capturing) => player.setCaptureActive(capturing)}
          />
        {/if}
      {/snippet}
    </PlayerComponent>
    {#if roomId}
      <div class="chips" class:obscured={playerModalOpen} inert={playerModalOpen} class:gone={!chromeVisible && !inviteOpen} style:--chip-inset="{chipInset}px">
        <!-- Invitations belong to the creator account, not the playback host. -->
        <button type="button" class="room" onclick={share} title="Invite someone to your room">
          <Icon name="share" size={15} />
          <span>Share</span>
        </button>
        {#if ChatPanel}
          <button
            type="button"
            class="room chat"
            aria-expanded={chatOpen}
            title={chatOpen ? 'Hide the chat' : 'Show the chat'}
            onclick={() => (chatOpen = !chatOpen)}
          >
            <Icon name="chat" size={15} />
            <span>Chat</span>
            {#if unread > 0 && !chatOpen}<i class="dot">{unread > 9 ? '9+' : unread}</i>{/if}
          </button>
        {/if}
      </div>
    {:else}
      <!-- The room opens itself with the player, so this branch is a beat of
           startup or a failed open. Either way the button is the same Share:
           pressing it opens the room if the automatic try could not. -->
      <button
        type="button"
        class="room solo"
        class:obscured={playerModalOpen}
        inert={playerModalOpen}
        onclick={share}
        disabled={makingRoom}
        title="Share a link that lands friends in this film"
      >
        <Icon name="share" size={15} />
        <span>{makingRoom ? 'Opening…' : 'Share'}</span>
      </button>
    {/if}
  </div>
{:else}
  <div class="waiting">
    <ArtworkWaiting {poster} backdrop={backdrop} title={heading} />
    <header class="bar">
      <button type="button" class="icon" onclick={() => back(exitHref)} aria-label="Back">
        <Icon name="back" size={20} />
      </button>
      <div class="what">
        <p class="name">{heading || '…'}</p>
        {#if subheading}<p class="sub">{subheading}</p>{/if}
      </div>
      <Button href="/welcome">About Jovii</Button>
      {#if roomId}
        <!-- The waiting screen's copy of the same Share: the link works before
             the film starts — whoever opens it waits in the same room. -->
        <button type="button" class="room inline" onclick={share}>
          <Icon name="share" size={15} />
          <span>Share</span>
        </button>
      {/if}
    </header>

    {#if roomError || metaError || linksError || playerStale || playerMissing}
    <div class="middle recovery">
      <div class="say">
        {#if roomError}
          <p class="bad" role="alert">{roomError}</p>
          <div class="acts"><Button href="/friends" icon="people">Friends</Button><Button href={exitHref} icon="back">Back</Button></div>
        {:else if metaError}
          <p class="bad"><Icon name="alert" size={15} /> {metaError}</p>
          <div class="acts"><Button href="/" icon="back">Home</Button></div>
        {:else if linksError}
          <p class="bad"><Icon name="alert" size={15} /> {linksError}</p>
          <div class="acts">
            <Button icon="link" variant="primary" onclick={openPicker}>Choose quality</Button>
            <Button href={exitHref} icon="back">Back</Button>
          </div>
        {:else if playerStale}
          <p class="bad"><Icon name="alert" size={15} /> Jovii has updated since this page was opened.</p>
          <p class="dim">One reload picks up the new version and your film plays as normal.</p>
          <div class="acts">
            <Button icon="refresh" variant="primary" onclick={() => location.reload()}>Reload and play</Button>
            <Button href={exitHref} icon="back">Back</Button>
          </div>
        {:else if playerMissing}
          <p class="bad"><Icon name="alert" size={15} /> This video could not start.</p>
          <p class="dim">Go back and try playing it again.</p>
          <div class="acts"><Button icon="link" onclick={openPicker}>Choose quality</Button></div>
        {/if}
      </div>
    </div>
    {/if}
  </div>
{/if}

{#if ChatPanel && chatTransport && roomId}
  {@const Chat = ChatPanel}
  <!--
    Kept MOUNTED while it is hidden and told `open={false}` instead, which is what
    its contract asks for: while it is closed the store counts unread rather than
    clearing it, and that count is the dot on the button above.
  -->
  <aside class="chat-drawer" class:open={chatOpen} aria-hidden={!chatOpen} inert={!chatOpen}>
    <Chat
      transport={chatTransport}
      {roomId}
      open={chatOpen}
      compact
      heading=""
      onunread={(n) => (unread = n)}
    />
    <button type="button" class="shut" onclick={() => (chatOpen = false)} aria-label="Hide the chat">
      <Icon name="close" size={17} />
    </button>
  </aside>
{/if}

{#if selector}
  <SourceSheet
    bind:open={pickerOpen}
    {selector}
    heading={heading + (subheading ? ` · ${subheading}` : '')}
    onclose={closePicker}
  />
{/if}

</WatchViewport>

<style>
  .host {
    position: relative;
    min-height: var(--watch-height, 100dvh);
  }

  .room {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding-inline: 10px;
    border-radius: 99px;
    border: 1px solid color-mix(in oklab, var(--accent) 45%, var(--line));
    background: color-mix(in oklab, var(--accent) 20%, rgb(6 8 12 / 0.7));
    color: var(--text);
    font-family: var(--mono);
    font-size: 0.72rem;
    font-weight: 600;
  }
  /*
   * `right` lives HERE and only here.
   *
   * The offset was first written as a bare `.chips { right: … }` below, and it did
   * nothing: `.host > .chips` is (0,2,0) against (0,1,0), so the flat 12px kept
   * winning and the chips sat under the drawer exactly as before. Measured — chips
   * 12px from the window edge with the drawer occupying the right 340, and
   * `elementFromPoint` at the chip's own centre returning the drawer. One owner of
   * one number, in one rule.
   */
  .host > .room,
  .host > .chips {
    position: fixed;
    top: max(10px, var(--safe-t));
    z-index: var(--z-room-chip);
    /* Slide left by whatever the chat drawer has claimed off the stage, so the chips
       ride on the film rather than under the panel. Zero when it is shut, and zero on
       a phone, where the drawer is a bottom sheet and claims height instead. */
    right: calc(max(12px, var(--safe-r)) + var(--chip-inset, 0px));
    transition:
      right var(--dur-slow) var(--ease-out),
      opacity var(--dur) var(--ease-out);
  }
  /* Fades with the player's chrome, off the same timer. See `chromeVisible` above. */
  /* Hide immediately while a player panel owns focus, including the solo Share.
     Opacity alone leaves a fading control painted over the panel's close button. */
  .host > .chips.obscured,
  .host > .room.obscured {
    visibility: hidden;
    pointer-events: none;
  }
  .host > .chips.gone {
    opacity: 0;
    pointer-events: none;
  }
  @media (prefers-reduced-motion: reduce) {
    .host > .room,
    .host > .chips {
      transition: none;
    }
  }
  .chips {
    display: flex;
    gap: 6px;
  }

  .chat .dot {
    display: grid;
    place-content: center;
    min-width: 16px;
    height: 16px;
    margin-left: 2px;
    padding-inline: 4px;
    border-radius: var(--radius-pill);
    background: var(--accent);
    color: var(--accent-ink);
    font-family: var(--font);
    font-size: 10px;
    font-style: normal;
    font-weight: var(--weight-bold);
  }

  /*
   * The drawer is a SIBLING of the player, never a child: the player owns its own
   * chrome, its idle timer and its full-screen element, and nesting ours inside
   * any of those would put two owners on one number.
   *
   * That has one consequence worth writing down: in the iPhone's native full
   * screen — and in any DOM full screen taken on the player's stage — this is not
   * painted, because a full-screen element paints in the top layer above
   * everything outside its subtree. Chat is a between-scenes thing; the film
   * winning the screen is correct.
   */
  .chat-drawer {
    position: fixed;
    z-index: calc(var(--z-room-chip) + 1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: color-mix(in oklab, var(--bg) 88%, transparent);
    backdrop-filter: var(--glass-blur);
    border: 1px solid var(--glass-line);
    box-shadow: var(--shadow-3);
    transition:
      transform var(--dur-slow) var(--ease-out),
      opacity var(--dur) var(--ease);
  }
  .chat-drawer:not(.open) {
    opacity: 0;
    pointer-events: none;
  }

  /* Phone: a bottom sheet, because a side drawer on 390px is the whole screen. */
  .chat-drawer {
    left: 0;
    right: 0;
    bottom: 0;
    height: min(calc(var(--watch-height, 100dvh) * .64), 460px);
    padding-bottom: var(--safe-b);
    padding-inline: var(--safe-l) var(--safe-r);
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    transform: translateY(101%);
  }
  .chat-drawer.open {
    transform: translateY(0);
  }

  .shut {
    position: absolute;
    top: 8px;
    right: 8px;
    display: grid;
    place-content: center;
    width: var(--hit);
    height: var(--hit);
    border-radius: var(--radius-pill);
    color: var(--text-dim);
  }
  .shut:hover {
    background: var(--surface-2);
    color: var(--text);
  }

  @container watch (min-width: 768px) {
    /* Tablet and up: a column beside the film, which is what "compact" is for. */
    .chat-drawer {
      left: auto;
      top: 0;
      bottom: 0;
      width: 340px;
      height: auto;
      padding-bottom: 0;
      border-radius: var(--radius-xl) 0 0 var(--radius-xl);
      transform: translateX(101%);
    }
    .chat-drawer.open {
      transform: translateX(0);
    }
  }
  .room:hover {
    background: color-mix(in oklab, var(--accent) 32%, rgb(6 8 12 / 0.7));
  }

  /* Not yet a group: an invitation, not a status. Quieter than a live room code. */
  .room.solo {
    border-color: var(--line);
    background: rgb(6 8 12 / 0.72);
    color: var(--text-dim);
    font-family: var(--font);
    font-weight: 500;
  }
  .room.solo:hover:not(:disabled) {
    background: var(--surface-2);
    color: var(--text);
  }
  .room:disabled {
    opacity: 0.6;
  }

  .waiting {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: var(--watch-height, 100dvh);
    background: var(--bg);
  }

  .recovery { position: relative; z-index: 2; background: rgb(0 0 0 / .6); }
  .bar {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: max(8px, var(--safe-t, env(safe-area-inset-top))) max(var(--page-pad), var(--safe-r)) 8px max(var(--page-pad), var(--safe-l));
  }
  .icon {
    display: grid;
    place-content: center;
    width: 36px;
    height: 36px;
    flex: none;
    border-radius: 99px;
    color: var(--text-dim);
  }
  .icon:hover {
    background: var(--surface-2);
    color: var(--text);
  }
  .what {
    flex: 1;
    min-width: 0;
  }
  .name {
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sub {
    color: var(--text-faint);
    font-size: 0.74rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .middle {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 22px;
    padding: 24px var(--page-pad) 52px;
    text-align: center;
  }
  .say {
    display: grid;
    justify-items: center;
    gap: 8px;
    max-width: 48ch;
  }
  .bad {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--warn);
    font-size: 0.88rem;
    font-weight: 600;
    text-wrap: balance;
  }
  .dim {
    color: var(--text-faint);
    font-size: 0.79rem;
    line-height: 1.55;
    text-wrap: pretty;
  }
  .acts {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    margin-top: 6px;
  }

  @container watch (min-width: 768px) {
    .middle {
      flex-direction: row;
      gap: 34px;
      text-align: left;
    }
    .say {
      justify-items: start;
    }
    .acts {
      justify-content: flex-start;
    }
  }
</style>
